import React, { Component, type ErrorInfo, type ReactNode } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import App from './App'
import './styles/globals.css'

// ─── ErrorBoundary racine ────────────────────────────────────────────────────
// Attrape toute erreur React fatale pour éviter un écran blanc.

class RootErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error?: Error }
> {
  state: { hasError: boolean; error?: Error } = { hasError: false, error: undefined }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Root error:', error, info)
    import('@capacitor-firebase/crashlytics')
      .then(({ FirebaseCrashlytics }) => {
        FirebaseCrashlytics.recordException({
          message: error.message,
          stacktrace: [{
            fileName: info.componentStack?.slice(0, 200) ?? 'main.tsx',
            lineNumber: 0,
            functionName: 'RootErrorBoundary.render',
          }],
        }).catch(() => {})
      })
      .catch(() => {})
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          textAlign: 'center',
          background: '#000',
          color: '#fff',
          fontFamily: 'Inter, sans-serif',
        }}>
          <h1 style={{ fontSize: 20, marginBottom: 12 }}>Une erreur est survenue</h1>
          <p style={{ fontSize: 14, opacity: 0.7, marginBottom: 24 }}>
            {this.state.error?.message || 'Erreur inconnue'}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              background: '#fff',
              color: '#000',
              border: 'none',
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Réessayer
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// ─── Init native non-bloquante ───────────────────────────────────────────────
// Tous les plugins natifs sont chargés en parallèle sans bloquer le render React.

if (Capacitor.isNativePlatform()) {
  // Status bar
  StatusBar.setStyle({ style: Style.Dark }).catch(() => {})

  // Firebase Crashlytics
  import('@capacitor-firebase/crashlytics')
    .then(({ FirebaseCrashlytics }) => {
      FirebaseCrashlytics.setEnabled({ enabled: true }).catch(() => {})
    })
    .catch(err => console.warn('[Crashlytics] init failed:', err))

  // Keyboard
  import('@capacitor/keyboard')
    .then(({ Keyboard, KeyboardResize }) => {
      Keyboard.setResizeMode({ mode: KeyboardResize.Body }).catch(() => {})
      Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {})
      Keyboard.setScroll({ isDisabled: false }).catch(() => {})
      Keyboard.addListener('keyboardWillShow', () => {
        document.body.classList.add('has-keyboard-open')
      })
      Keyboard.addListener('keyboardWillHide', () => {
        document.body.classList.remove('has-keyboard-open')
      })
    })
    .catch(err => console.warn('[Keyboard] init failed:', err))

  // Splash screen — filet de sécurité si React ne monte pas
  setTimeout(() => {
    import('@capacitor/splash-screen')
      .then(({ SplashScreen }) => {
        SplashScreen.hide().catch(() => {})
      })
      .catch(() => {})
  }, 3000)
}

// ─── Render ──────────────────────────────────────────────────────────────────

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RootErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </RootErrorBoundary>
  </React.StrictMode>,
)
